/**
 * 논서술형(essay) 챕터 풀이 화면(학생 EssayPlay)이 쓰는 응답 타입 + JSON 파싱 헬퍼 모음.
 * 관리자가 만든 블록 데이터를 BE가 내용을 들여다보지 않고 그대로 통과(opaque)시켜 주면,
 * 이 파일의 parse* 함수들이 학생 화면이 바로 쓸 수 있는 형태로 풀어낸다.
 */
// BE 응답 shape (user-api: /app/essay-learning/**)

// ────────────────────────────────────────────────────────────────────────────
// Chapter 응답 (GET /app/essay-learning/chapters/{id})
// ────────────────────────────────────────────────────────────────────────────

/** 학생 응답. blockId 단위 답안 복원용 */
export interface SavedAnswerDto {
  blockId: string;
  valueJson: string;
  lockedAt: string | null;
  /** pin_keypad 블록의 시도/잠금 파생 상태. pin 블록이 아니면 null. ★ 정답값 미포함. */
  pinStatus?: PinStatusDto | null;
}

/** pin_keypad 시도/잠금 누적 상태. ★ 정답값 미포함. */
export interface PinStatusDto {
  /** 해제 상태(누적). 정답 1회 이후 true. */
  unlocked: boolean;
  /** 3회 소진 잠금(`remainingAttempts==0 && !unlocked`). */
  locked: boolean;
  /** 누적 사용 시도(0~3) */
  attemptsUsed: number;
  /** 남은 시도(0~3) */
  remainingAttempts: number;
}

/**
 * 문항 DTO.
 * ★ `interactionAnswerKey` / `interactionAnswerKeyJson` 필드 절대 추가 금지(보안 불변식).
 */
export interface EssayQuestionDto {
  id: number;
  displayOrder: number;
  /** 문항 짧은 제목(미션 카드용). null이면 시나리오 제목 폴백. */
  title?: string | null;
  prompt: string;
  minChars: number | null;
  recMinChars: number | null;
  recMaxChars: number | null;
  /** 본문 단일 이미지 URL. drag_pin 블록의 config.image가 null이면 이 값으로 fallback. */
  imageUrl: string | null;
  /** 학생 노출 인터랙션 블록 JSON 배열 (8종 union, answer 필드 없음) */
  interactionBlocksJson: string;
  /** 자료영역 헤더 자료명(이모지+텍스트 평문). null이면 헤더 미렌더. */
  materialName?: string | null;
  /** 자료영역 헤더 자료 설명(자료명 아래 한 줄). null/빈 문자열이면 미렌더. */
  materialDescription?: string | null;
  /** 문제 레벨 material 4종(tabs/cards/image/text) JSON 배열. 구 응답 호환 위해 옵셔널. */
  materialBlocksJson?: string;
  /** 문제 시작 전 영상. null/빈 문자열이면 skip. */
  preMediaUrl?: string | null;
  /** 문제 종료 후 영상. null/빈 문자열이면 skip. */
  postMediaUrl?: string | null;
  /** 문제별 진행 버튼 라벨. null/빈 문자열이면 "다음 문항" 기본값. */
  actionLabel?: string | null;
  /**
   * 문항 제한시간(초). null/없음이면 시간 없는 문항이다.
   * 한 미션 안 문항은 모두 있거나 모두 없다(서버가 배정 때 막는다). 있으면 그 미션은 미션 타이머 대신
   * 문항 타이머로 진행하고, 직전 문항이 이 값을 가지면 "← 이전"으로 돌아갈 수 없다.
   */
  timeLimitSec?: number | null;
  /** 앞 문항에서 일찍 넘어와 이월받은 초. 없으면 0 으로 본다. */
  carryInSec?: number;
  /**
   * 일시정지·heartbeat 로 서버에 남은 이 문항의 남은 초. 시간 있는 문항만 값이 있다.
   * 시작 남은 시간 = `remainSec ?? timeLimitSec + carryInSec`.
   */
  remainSec?: number | null;
  /** 재진입 시 답안 복원. blockId 단위 */
  savedAnswers: SavedAnswerDto[];
}

export interface EssayScenarioDto {
  id: number;
  displayOrder: number;
  /** 시나리오 입구 화면 서브 인용(~500자). */
  code: string;
  title: string;
  /** "C"/"T"/"S"/"W"/"M"/"E" */
  competencyCode: string | null;
  badge: string | null;
  /** 시나리오 입구 메인 액션 버튼 라벨. null/빈 문자열이면 "미션 시작" 기본값. */
  introActionLabel?: string | null;
  /** 인트로 이모지 클러스터 톤(amber/red/pink/blue). emojis와 함께 비면 영역 숨김. */
  introEmojiTone?: string | null;
  /** 정확히 4개. null/빈 배열이면 클러스터 영역 자체 숨김. */
  introEmojiEmojis?: string[] | null;
  /** 미션 배지 텍스트. null/빈 문자열이면 배지 영역 비표시. */
  introMissionLabel?: string | null;
  /** 시나리오별 카운트다운 초 (기본 810s = 13:30) */
  scenarioTimeLimitSec: number;
  /** 표지 화면 제한시간(초). null/undefined 면 제한 없음. 0 이 되면 문항 화면으로 자동 진입 */
  introTimeLimitSec?: number | null;
  /** 미션 완료(다음 미션 안내) 화면 제한시간(초). null/undefined 면 제한 없음. 0 이 되면 다음 미션으로 자동 진행 */
  outroTimeLimitSec?: number | null;
  scenarioStartedAt: string | null;
  scenarioSubmittedAt: string | null;
  introCharacterDialogueJson: string | null;
  outroCardJson: string | null;
  /** material 4종 (tabs/cards/image/text) JSON */
  materialBlocksJson: string;
  /** 시나리오 시작 전 영상. null/빈 문자열이면 skip. */
  preMediaUrl: string | null;
  /** 시나리오 종료 전 영상. null/빈 문자열이면 skip. */
  postMediaUrl: string | null;
  /**
   * 시나리오별 freeze 된 "남은 초"(절대 deadline이 아니다).
   * null이면 임시중단 이력 없음(scenarioTimeLimitSec 전체 사용).
   * 값이 있으면 FE가 `deadline = now + remainSec`로 타이머를 재계산한다.
   */
  remainSec?: number | null;
  questions: EssayQuestionDto[];
}

/** `GET /app/essay-learning/chapters/{id}` 응답. */
export interface EssayChapterDetailResponse {
  chapterId: number;
  name: string;
  chapterStartedAt: string | null;
  submitDate: string | null;
  /**
   * 재진입 복원 상태.
   * "NONE" = 정상 진행 또는 소비됨(크래시 이탈도 여기), "PAUSED" = 긴급 정지 중, "STOPPED" = 그만 풀기,
   * "CLOSED" = 창 닫기·새로고침·뒤로 가기(학생이 스스로 나감. 시간은 흐른다).
   * 구 BE 응답(필드 부재)은 undefined → NONE으로 간주한다.
   */
  suspendState?: EssaySuspendState | null;
  /** 그만풀기/일시정지/창 닫기 시점 화면 위치 JSON. null이면 localStorage 폴백. */
  resumeScreenJson?: string | null;
  /** 마지막 suspend 시각(서버 시계). CLOSED 복원 때 serverNow 와 빼서 닫혀 있던 시간을 구한다. */
  suspendedAt?: string | null;
  /** 마지막 heartbeat 시각(서버 시계). */
  lastSeenAt?: string | null;
  /** 응답 생성 시각(서버 시계). */
  serverNow?: string | null;
  /**
   * 관리자가 템플릿에 켠 "도입문의 받기". 할당 시점에 챕터로 복사된 스냅샷이라, 나중에 템플릿의 값을
   * 바꿔도 이미 할당된 챕터에는 반영되지 않는다. true 면 완료 화면이 영업용으로 바뀐다(문의하기·리포트
   * 샘플 보기). 구 BE 응답(필드 부재)은 undefined → 꺼짐으로 본다.
   */
  adoptionInquiryEnabled?: boolean | null;
  /**
   * 문항 남은 시간 이월 여부. 챕터 시작 순간의 운영 설정 값으로 고정된다.
   * 꺼져 있으면 일찍 넘어가도 남은 시간이 다음 문항에 더해지지 않는다. 구 BE 응답(필드 부재)은 꺼짐으로 본다.
   */
  carryOverEnabled?: boolean;
  scenarios: EssayScenarioDto[];
}

export type EssaySuspendState = "NONE" | "PAUSED" | "STOPPED" | "CLOSED";

/** resumeScreenJson 표준 형태. 그만풀기/일시정지 시점 화면 위치(서버 보조 캐시). */
export interface EssayResumeScreen {
  screen: string;
  scenarioIdx: number;
  questionIdx: number;
}

/** resumeScreenJson 문자열을 파싱한다. 형식이 어긋나면(예전 데이터 등) null을 반환한다. */
export function parseResumeScreen(
  json: string | null | undefined
): EssayResumeScreen | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as Partial<EssayResumeScreen>;
    if (
      typeof parsed.screen === "string" &&
      typeof parsed.scenarioIdx === "number" &&
      typeof parsed.questionIdx === "number"
    ) {
      return {
        screen: parsed.screen,
        scenarioIdx: parsed.scenarioIdx,
        questionIdx: parsed.questionIdx,
      };
    }
    return null;
  } catch {
    return null;
  }
}

// POST /app/essay-learning/chapters/{id}/suspend (그만풀기/일시정지 bulk 임시저장)

/** suspend 사유. PAUSED/STOPPED 는 긴급 정지 버튼, CLOSED 는 창 닫기·새로고침·뒤로 가기. */
export type EssaySuspendReason = "PAUSED" | "STOPPED" | "CLOSED";

export interface SuspendBlockAnswer {
  questionId: number;
  blockId: string;
  /** null/생략이면 답안 저장 스킵(상태/잔여시간만). */
  valueJson?: string;
}

export interface SuspendChapterRequest {
  suspendReason: EssaySuspendReason;
  /** 현재 입력 중 블록 답안 bulk. 빈 배열 허용. */
  blocks?: SuspendBlockAnswer[];
  /** ★ 시나리오별 "남은 초"(절대시각 아님, CASE8). key=시나리오 id, value=남은 초. */
  remainSecByScenario?: Record<number, number>;
  /** 시간 있는 문항의 "남은 초". key=문항 id. 서버가 0 ~ (제한시간 + 이월) 로 잘라 저장한다. */
  remainSecByQuestion?: Record<number, number>;
  /** 그만풀기 시점 화면 위치 힌트(FE 직렬화). null/생략이면 서버 기존 값 유지. */
  resumeScreenJson?: string;
}

export interface SuspendChapterResponse {
  chapterId: number;
  suspendState: EssaySuspendState;
  savedBlockCount: number;
}

/** `POST /app/essay-learning/chapters/{id}/heartbeat` 요청. 문항 화면에서 주기적으로 보낸다. */
export interface HeartbeatRequest {
  /** 시간이 흐르고 있는 미션 id. 문항 화면이 아니면 생략. */
  scenarioId?: number;
  /** 그 미션의 남은 초. questionId 가 있으면 그 문항의 남은 초다. */
  remainSec?: number;
  /** 시간 있는 문항 화면이면 그 문항 id. 서버는 미션 대신 문항 남은 초로 저장한다. */
  questionId?: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Chapter Start/Submit
// ────────────────────────────────────────────────────────────────────────────

export interface ChapterStartResponse {
  chapterId: number;
  chapterStartedAt: string;
}

export interface ChapterSubmitResponse {
  chapterId: number;
  submitDate: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Scenario Start/Timeout
// ────────────────────────────────────────────────────────────────────────────

export interface ScenarioStartResponse {
  scenarioId: number;
  scenarioStartedAt: string;
  scenarioTimeLimitSec: number;
}

export interface ScenarioTimeoutResponse {
  scenarioId: number;
  scenarioSubmittedAt: string;
  /** null = 마지막 시나리오. chapterCompleted=true와 함께 옴 */
  nextScenarioId: number | null;
  /** true = 챕터 자동 submit 됨 */
  chapterCompleted: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Block Answer (Save/Lock)
// ────────────────────────────────────────────────────────────────────────────

export interface SaveBlockAnswerRequest {
  blockId: string;
  valueJson: string;
}

export interface SaveBlockAnswerResponse {
  essayAnswerId: number;
  essayInteractionAnswerId: number | null;
  blockId: string | null;
  submittedAt: string | null;
  lockedAt: string | null;
  /** 잠금 응답 전용. 같은 미션의 다음 문항 id. 없으면 null. */
  nextQuestionId?: number | null;
  /** 잠금 응답 전용. 다음 문항이 이월받은 초. 다음 문항이 없거나 이월이 꺼져 있으면 0. */
  nextCarryInSec?: number;
}

/**
 * 문항 잠금 요청 본문(`POST /questions/{id}/lock`). 모두 선택값이다.
 * 이미 잠긴 문항이면 서버가 둘 다 무시하고 같은 형태로 응답한다(멱등).
 */
export interface LockQuestionRequest {
  /** 이 문항의 화면 최종 답안 칸. 다른 문항 id 가 섞이면 404, valueJson 4000자 초과면 400. */
  blocks?: SuspendBlockAnswer[];
  /** 이 문항에서 남은 초. 시간 종료면 0. 이월이 켜져 있으면 다음 문항 이월 초가 된다. */
  remainSec?: number;
}

// ────────────────────────────────────────────────────────────────────────────
// 인터랙션 블록 7종. discriminated union by `type`
// ────────────────────────────────────────────────────────────────────────────

export type InteractionBlock =
  | TextareaInteractionBlock
  | PinKeypadInteractionBlock
  | CardSelectSingleInteractionBlock
  | CardSelectMultiInteractionBlock
  | DragReorderInteractionBlock
  | DragPinInteractionBlock
  | MaterialTabsInteractionBlock
  | VideoCinematicInteractionBlock
  | EssentialTriageInteractionBlock
  | BudgetAllocationInteractionBlock;

export interface TextareaInteractionBlock {
  type: "textarea";
  blockId: string;
  label?: string;
  placeholder?: string;
  /**
   * 입력 하한(미만 시 경고). 관리자 블록 config.minChars 가 BE 평탄화로 그대로 전달됨.
   */
  minChars?: number;
  /**
   * 권장 최소/최대 글자수.
   *
   * 관리자 InteractionBlockEditor.TextareaBlockForm 이 config.recMin/recMax 로 저장하고,
   * BE EssayQuestionDto 평탄화가 같은 키(recMin/recMax)로 top-level 에 전달한다.
   * minChars=하한(미만 경고), recMin~recMax=권장 범위(글자수 카운터·색상·안내에 반영).
   */
  recMin?: number;
  recMax?: number;
  /**
   * 절대 상한(초과 차단). 관리자 UI 에 입력란이 없어 보통 부재. 부재면 상한 없음.
   * 권장 최대(recMax)와 구분된다(recMax 초과는 경고만, maxChars 초과는 차단).
   */
  maxChars?: number;
  /**
   *  (요구4-b). 답변 칸별 개별 힌트.
   *
   * ★ 키 경로 = `block.config.hint`(string).  정합. 관리자
   *   InteractionBlockEditor 가 textarea 블록 config 안 `hint` 키로 직렬화하고,
   *   BE 는 interactionBlocksJson 을 opaque LONGTEXT 로 통과시킨다(BE 코드 0줄).
   *   minChars/recMin 처럼 top-level 로 평탄화되지 **않는다**. config 안에 그대로 남는다.
   * 문항 단위(본문 레벨) hint 폴백 제거.
   *   자율서술답변(textarea)마다 블록 레벨 hint 가 있어 폴백이 불필요해짐(사용자 확정).
   *
   *  ★ hint 는 이제 top-level 키다.
   *   BE EssayQuestionDto 평탄화(flattenByType textarea 케이스)가 `block.remove("config")`
   *   로 config 래퍼를 통째로 지우면서 config.hint 가 유실되던 버그를 BE 가 수정. 이제
   *   minChars/recMin 처럼 hint 도 top-level 로 copy 해 내려준다.
   *   학생 렌더(TextareaBlock)는 `block.hint`(top-level)를 본다. 미설정이면 hint 키가 없다
   *   → 힌트 박스 자체를 렌더하지 않는다. 구버전 캐시/응답 호환을 위해 config.hint 도 폴백한다.
   */
  hint?: string;
  /** "형식 보기" 접이식 안내 항목 배열. 빈 배열이면 토글/박스 미렌더. */
  formatGuide?: string[];
  config?: {
    hint?: string;
  };
}

export interface PinKeypadInteractionBlock {
  type: "pin_keypad";
  blockId: string;
  label?: string;
  /**
   *  (후속, 버그①). 좌측 자료 탭 이름.
   * 어드민이 자료영역 블록으로 추가할 때 설정한 탭 라벨("숫자 맞추기" 등). 있으면 우선 사용,
   * 없으면 유형명("비밀번호") 폴백. MaterialBlockBase.tabLabel 과 같은 의미.
   */
  tabLabel?: string;
  /** 자릿수 (기본 4) */
  length?: number;
}

// ────────────────────────────────────────────────────────────────────────────
//
//   pin_keypad 즉시 정오답 피드백 + 3회 시도 제한.
//   POST /app/essay-learning/questions/{questionId}/blocks/{blockId}/pin-attempt
//    시그니처. 정답값(pins/correctValue)은 응답·상태에 절대 없음.
// ────────────────────────────────────────────────────────────────────────────

/** pin-attempt 요청. 학생이 입력한 자릿수 배열만. wrongAttempts 는 보내지 않는다(서버 저장값만 신뢰). */
export interface PinAttemptRequest {
  pins: number[];
}

/** pin-attempt 응답 result. 누적 상태(정답값 미포함). */
export interface PinAttemptResponse {
  /** 이번/누적 정답 여부(정답 1회 이후 멱등 true). */
  correct: boolean;
  /** 해제 상태(누적). */
  unlocked: boolean;
  /** 남은 시도(0~3). */
  remainingAttempts: number;
  /** 3회 소진 잠금(`remainingAttempts==0 && !unlocked`). */
  locked: boolean;
  /** 누적 사용 시도(0~3). 시도 점 렌더용. */
  attemptsUsed: number;
}

/** 시도 제한(3회). BE `EssayPinAttemptService.ATTEMPTS_MAX` 와 동기화된 코드 상수. */
export const PIN_ATTEMPTS_MAX = 3;

/**
 * 저장 valueJson 에서 pin 상태를 파생한다(BE 가 pinStatus 를 안 내리는 레거시 응답 대비).
 *
 * BE 와 동일 공식:
 *   attemptsUsed = min(wrongAttempts, 3)
 *   remainingAttempts = 3 - attemptsUsed
 *   locked = !unlocked && remainingAttempts == 0
 * 정답값은 입력하지 않는다. wrongAttempts/unlocked 마커만 사용.
 */
export const derivePinStatus = (
  wrongAttempts: number,
  unlocked: boolean
): PinStatusDto => {
  const attemptsUsed = Math.min(
    Math.max(Math.trunc(wrongAttempts) || 0, 0),
    PIN_ATTEMPTS_MAX
  );
  const remainingAttempts = PIN_ATTEMPTS_MAX - attemptsUsed;
  return {
    unlocked,
    locked: !unlocked && remainingAttempts === 0,
    attemptsUsed,
    remainingAttempts,
  };
};

export interface CardSelectOption {
  optionId: string;
  label: string;
  description?: string;
}

/**
 * `card_select_single` 직접 입력 예약 토큰.
 *
 * `selected` 가 이 값이면 프리셋 칩이 아니라 학생 자유 텍스트(`customText`)를 쓴 상태다.
 * BE(`EssayGradingPromptBuilder`)와 1:1 합의된 예약 optionId. 관리자 카드 id 로 쓰면 충돌
 * (관리자 id 는 `c1`/`c2` 자동 생성이라 실사용 충돌 가능성 낮음).
 */
export const CARD_CUSTOM_SELECTED = "__custom__";

/**
 * `card_select_single` 학생 응답 객체 계약.
 *
 * ★ 이전에는 raw 문자열 optionId(`"c2"`)로 저장돼 BE `.path("selected")` 가 빈칸으로 들어가는
 *   라이브 채점 버그가 있었다. 이제 항상 객체로 저장한다.
 *   - 프리셋 칩 선택: `{ selected: "c2" }`
 *   - 직접 입력 선택: `{ selected: "__custom__", customText: "..." }`
 *   - 미선택(방어적): `{ selected: "" }`
 * 레거시 raw string 복원값은 렌더 캐스팅 지점(InteractionBlockRenderer)에서 `{ selected }` 로 승격한다.
 */
export interface CardSelectSingleValue {
  /** 프리셋이면 카드 optionId, 직접 입력이면 예약 토큰 `"__custom__"`, 미선택이면 빈 문자열. */
  selected: string;
  /** 직접 입력일 때만 동반하는 자유 텍스트. */
  customText?: string;
}

export interface CardSelectSingleInteractionBlock {
  type: "card_select_single";
  blockId: string;
  label?: string;
  options: CardSelectOption[];
  /** 풀이 블록 공통 힌트. 미설정이면 키 부재(힌트 박스 미렌더). */
  hint?: string;
  /**
   * 직접 입력(주관식) 허용 토글.
   *
   * ★ 키 경로 = top-level `allowCustomInput`(boolean). hint 와 동일하게 BE 평탄화
   *   (flattenByType card_select_single 케이스)가 config.allowCustomInput 을 top-level 로
   *   copy 해 내려준다. false/부재면 "직접 입력" 버튼·textarea 미노출.
   */
  allowCustomInput?: boolean;
  /**
   * 선택지 배치.
   *
   * - `"chips"`(기본·키 부재) — 한 줄에 균등 분배되는 칩. 짧은 라벨용이다.
   * - `"list"` — 한 줄에 하나씩 쌓는 세로 카드. 라벨이 문장 여러 개라 칩에 넣으면 줄이 깨지는
   *   문항용(체험하기 1차 심사 Q1-A). 앞에 순번 배지를 붙여 서로 가리키기 쉽게 한다.
   *
   * ★ 관리자 에디터에는 아직 이 토글이 없다. 체험하기 상수(`playEssayMissions.ts`)가 직접 켜는
   *   값이라 BE 평탄화 대상도 아니다. 키가 없으면 기존 칩 그대로라 실검사 문항은 영향이 없다.
   */
  layout?: "chips" | "list";
}

export interface CardSelectMultiInteractionBlock {
  type: "card_select_multi";
  blockId: string;
  label?: string;
  options: CardSelectOption[];
  minSelect?: number;
  maxSelect?: number;
  /**
   * 선택지 배치. `"grid"`(기본·키 부재)는 2열 카드, `"list"` 는 한 줄에 하나씩 쌓는다.
   * 선택지가 문장 여러 개면 2열에서 폭이 모자라 읽기 어렵다(체험하기 3차 심사 Q3).
   */
  layout?: "grid" | "list";
  /** 풀이 블록 공통 힌트. CardSelectSingle 동일. */
  hint?: string;
}

export interface DragReorderItem {
  itemId: string;
  /**
   *  보기 item 2칸 분리: title(명령 라벨 칩, 예 "IF"/"THEN2") + content(본문).
   *   둘 다 옵셔널. 구버전 데이터는 label 만 있다(title/content 부재). 학생 렌더는
   *   title 이 있으면 칩으로, 본문은 content ?? label 로 표시한다(하위호환).
   *   ★ 정답 매칭 키는 itemId(불변). title/content 텍스트는 매칭에 쓰지 않는다.
   *   ★ BE 의존: drag_reorder 도 평탄화 시 remapReorderItems 가 title/content 를 copy 해야
   *     학생 응답에 실린다.
   */
  title?: string;
  content?: string;
  /** (구) 단일 라벨. title/content 도입 전 데이터. content 부재 시 본문 폴백. */
  label?: string;
}

export interface DragReorderInteractionBlock {
  type: "drag_reorder";
  blockId: string;
  label?: string;
  /**
   *  (후속, 버그①). 좌측 자료 탭 이름.
   * legacy 데이터가 materialBlocksJson 의 자료영역 블록으로 저장될 때 설정한 탭 라벨.
   * 있으면 우선 사용, 없으면 유형명("순서 맞추기") 폴백.
   */
  tabLabel?: string;
  /** 초기 순서. 학생이 드래그로 재정렬 */
  items: DragReorderItem[];
  /**
   * 몇 번째인지 번호를 앞에 붙일지. 기본(키 부재)은 안 붙인다.
   *
   * 순서 자체가 답이고 "1번"을 문항이 직접 가리키는 문항에서 켠다(체험하기 최종 심사 —
   * "1번이 제일 맡고 싶은 자리"). 번호가 없으면 학생이 세어 봐야 지금 1번이 뭔지 안다.
   */
  showRank?: boolean;
  /** 풀이 블록 공통 힌트. CardSelectSingle 동일. */
  hint?: string;
}

/**
 * drag_pin. 좌표형 드래그 정답 블록.
 *
 * 학생은 trough에서 라벨을 들어 이미지 위 자유 좌표에 놓는다.
 * 정답 좌표(correctPins)는 user-api 응답에 절대 포함되지 않는다 (서버 전용).
 * config.image가 null이면 부모 question.imageUrl로 fallback (호출자 책임).
 */
export interface DragPinItem {
  /** 식별자(pinId). items / pins / answerKey 공통 key */
  id: string;
  /** 학생 노출 라벨 */
  label: string;
}

export interface DragPinInteractionBlock {
  type: "drag_pin";
  blockId: string;
  label?: string;
  config: {
    /** 드래그할 라벨 목록 (정답 좌표는 서버 전용. 학생 응답에 부재) */
    items: DragPinItem[];
    /** drag_pin 전용 배경 이미지. null이면 question.imageUrl로 fallback */
    image: { publicUrl: string } | null;
  };
}

/** drag_pin 학생 응답. pinId → 원본 이미지 좌표 (px) */
export type DragPinValue = Record<string, { x: number; y: number }>;

export interface MaterialTabsInteractionBlock {
  type: "material_tabs";
  blockId: string;
  tabs: { tabId: string; title: string; content: string }[];
}

export interface VideoCinematicInteractionBlock {
  type: "video_cinematic";
  blockId: string;
  publicUrl: string;
  caption?: string;
  /** 끝까지 봐야 다음 버튼 활성 (기본 true) */
  forceWatchToEnd?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// 풀이영역 인터랙션 블록 2종 신규.
//
// ★ BE 평탄화 계약과 1:1 합의:
//   user-api flattenByType 이 admin 저장형 `{id,type,label,config}` 을 풀어
//   top-level + item `id→itemId` 로 내려준다. drag_reorder 패턴(itemId)과 동일.
//   학생 answer valueJson 키도 동일하게 `itemId` 를 쓴다.
// ★ BE 는 interactionBlocksJson 을 opaque LONGTEXT 로 통과. 신규 엔드포인트/컬럼 0.
//   학생 답안 저장은 기존 onChange → EssayInteractionAnswer.valueJson 경로 그대로.
// ────────────────────────────────────────────────────────────────────────────

/** essential_triage 항목 (평탄 schema, id→itemId) */
export interface EssentialTriageItem {
  itemId: string;
  /** 항목명 (학생 노출, 필수) */
  label: string;
  /** 부가설명 (선택, 없으면 생략 렌더) */
  desc?: string;
}

/**
 * essential_triage. 핵심/불필요 이지선다 분류 블록.
 * 항목마다 [✅ 핵심] / [✗ 불필요] 토글 2개(라디오 동작, 둘 중 하나).
 * 정답키 없음. 학생이 무엇을 골랐는가 자체가 채점 인풋(AI 직렬화).
 */
export interface EssentialTriageInteractionBlock {
  type: "essential_triage";
  blockId: string;
  label?: string;
  items: EssentialTriageItem[];
  /** 풀이 블록 공통 힌트. CardSelectSingle 동일. */
  hint?: string;
}

/** essential_triage 학생 응답. itemId → 분류값. 미선택 항목은 키 부재. */
export type TriageChoice = "essential" | "unnecessary";
export interface EssentialTriageValue {
  selections: Record<string, TriageChoice>;
}

/** budget_allocation 도구 (평탄 schema, id→itemId) */
export interface BudgetAllocationItem {
  itemId: string;
  /** 도구명 (학생 노출, 필수) */
  name: string;
  /** 단가 (정수, 필수) */
  unitPrice: number;
  /** 아이콘 (선택, 이모지 문자열) */
  icon?: string;
  /** 설명 (선택) */
  desc?: string;
}

/**
 * budget_allocation. 예산 배분 스테퍼 블록.
 * 도구마다 [−] 수량 [+] 스테퍼 + 소계, 하단 실시간 합계, 예산 초과 시 빨간 경고.
 * ★ 합계·초과여부는 표시용. BE 채점은 quantities + config(unitPrice/budget)로 재계산(FE 값 미신뢰).
 * 결정 Q1: 예산 초과해도 제출 가능(경고만, 제출 차단 안 함).
 */
export interface BudgetAllocationInteractionBlock {
  type: "budget_allocation";
  blockId: string;
  label?: string;
  /** 총 예산 한도 (정수, 필수) */
  budget: number;
  /** 통화 라벨 (기본 "해봄머니", 부재 시 fallback) */
  currencyLabel?: string;
  items: BudgetAllocationItem[];
  /**
   * 무엇을 배분하는지. 문구만 바뀌고 계산은 같다.
   *
   * - `"shop"`(기본·키 부재) — 예산으로 도구를 산다. "단가 n/개", "사용 예산", "도구 선택 초기화".
   * - `"allocate"` — 총량을 칸에 나눠 넣는다(체험하기 2차 훈련 시간 나누기). 단가 줄을 숨기고
   *   "배분한 점수"로 읽는다. 살 물건이 아니라 내 강점 신고라 "도구"·"단가"가 뜻을 흐린다.
   */
  mode?: "shop" | "allocate";
  /**
   * 0보다 크게 배분해야 하는 최소 항목 수. 부재면 제약 없음.
   *
   * 체험하기 2차는 4다 — "훈련한 항목은 전부 심사한다" 는 설정이라 한 과목 올인을 막는다.
   * ★ 예산 초과와 같은 취급이다: 못 채우면 경고만 하고 제출은 막지 않는다(결정 Q1).
   */
  minItems?: number;
  /** 풀이 블록 공통 힌트. CardSelectSingle 동일. */
  hint?: string;
}

/**
 * budget_allocation 학생 응답. itemId → 수량(0 이상 정수).
 * total/overBudget 은 FE 표시 편의값으로 동봉(BE 는 재계산해 무시).
 */
export interface BudgetAllocationValue {
  quantities: Record<string, number>;
  total: number;
  overBudget: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// material 자료 블록 4종 (학생 입력 없음, materialBlocksJson)
// ────────────────────────────────────────────────────────────────────────────

export type MaterialBlock =
  | MaterialTabsBlock
  | MaterialCardsBlock
  | MaterialImageBlock
  | MaterialTextBlock;

/**
 * 자료영역 좌측 탭 공통 필드.
 * `tabLabel`이 있으면 좌측 탭 이름으로 우선 사용(요구 3·5, Q2=가).
 * 없으면 타입명 fallback(`materialTabLabel`). BE는 materialBlocksJson opaque passthrough.
 */
interface MaterialBlockBase {
  blockId: string;
  /** 자료영역 좌측 탭 이름. 없으면 타입명 fallback. */
  tabLabel?: string;
}

export interface MaterialTabsBlock extends MaterialBlockBase {
  type: "tabs";
  tabs: { tabId: string; title: string; content: string }[];
}

export interface MaterialCardsBlock extends MaterialBlockBase {
  type: "cards";
  cards: { cardId: string; title: string; body: string }[];
}

export interface MaterialImageBlock extends MaterialBlockBase {
  type: "image";
  /**
   *  (후속, 버그②). 미디어 자산 식별자.
   * 어드민이 업로드 시 저장하고, BE 가 학생 응답 조립 시 이 값으로 publicUrl 을 재서명한다
   * (presigned GET URL 만료 방지). 학생 렌더는 publicUrl 을 그대로 소비한다(BE 재서명값).
   * legacy 데이터(이 값 없음)는 저장 시점 publicUrl 그대로. 만료 시 재업로드 필요.
   */
  mediaAssetId?: number;
  publicUrl: string;
  alt?: string;
  caption?: string;
}

export interface MaterialTextBlock extends MaterialBlockBase {
  type: "text";
  body: string;
}

// ────────────────────────────────────────────────────────────────────────────
// 도움 함수: JSON 파싱 안전 가드
// ────────────────────────────────────────────────────────────────────────────

/**
 * 블록 식별자 정규화. `id` → `blockId` 흡수.
 *
 * 관리자(producer)는 블록을 `id` 필드로 직렬화하지만, 학생(consumer) 타입·렌더(QuestionLeftTabs,
 * 답안 값 키)는 `blockId` 를 읽는다. BE 는 두 JSON 을 opaque LONGTEXT 로 통과시켜 변환 지점이
 * 없으므로, 정규화하지 않으면 모든 블록의 `blockId` 가 undefined 가 된다.
 *   → 자료 탭 id 가 전부 `mat-undefined` 로 충돌해 자료 이미지 2개가 한 탭으로 묶이고,
 *     인터랙션 블록 답안도 `undefined` 키로 저장되는 잠복 버그가 생긴다.
 * 이미 `blockId` 가 있으면 그대로 둔다(round-trip·미래 BE 변환 대비).
 */
const normalizeBlockId = (b: unknown): unknown => {
  if (b && typeof b === "object" && !Array.isArray(b)) {
    const o = b as { blockId?: unknown; id?: unknown };
    if (o.blockId == null && typeof o.id === "string") {
      return { ...o, blockId: o.id };
    }
  }
  return b;
};

/** `interactionBlocksJson` (배열 직렬화) → InteractionBlock[]. 실패 시 [] */
export function parseInteractionBlocks(
  json: string | null | undefined
): InteractionBlock[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) {
      return (parsed as unknown[]).map(normalizeBlockId) as InteractionBlock[];
    }
    return [];
  } catch {
    return [];
  }
}

/** `materialBlocksJson` → MaterialBlock[] */
export function parseMaterialBlocks(
  json: string | null | undefined
): MaterialBlock[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) {
      return (parsed as unknown[]).map(normalizeBlockId) as MaterialBlock[];
    }
    return [];
  } catch {
    return [];
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 인터랙션 블록 재분류 (Q3)
//
// "인터랙션 블록" 추상화 폐기 → 모든 블록을 자료영역(material) 또는 풀이영역
// (solution) 둘 중 하나로 재분류한다. 그 외 사용자 미언급 타입은 런타임 skip.
//   - 풀이영역(우측 수직 스택)  = textarea / card_select_single / card_select_multi / drag_reorder
//   - 자료영역(좌측 탭, 조작 입력 유지) = pin_keypad
//   - 삭제(런타임 skip)         = drag_pin / material_tabs / video_cinematic
//
// ★ BE는 블록을 파싱하지 않는다(opaque LONGTEXT). 재분류는 전적으로 FE 책임.
// ★ 비가역 백필 금지. 기존 row 파괴 없이 렌더 시점에만 분기( fallback).
//
// (요구6). 순서 맞추기(drag_reorder)를
//   자료영역(좌측 탭) → 풀이영역(우측 수직 스택)으로 이동.  정합
//   (관리자도 drag_reorder 를 SOLUTION_AREA_TYPES 로 옮김). PIN(pin_keypad)은 자료영역 유지(Q2).
//   ★ 하위호환. 기존 데이터의 자료영역(materialBlocksJson) drag_reorder 는 그대로 보존되지만,
//     인터랙션 블록(interactionBlocksJson) 안의 drag_reorder 는 이제 우측 풀이영역에 렌더된다.
// ────────────────────────────────────────────────────────────────────────────

/** 풀이영역(우측 수직 스택) 블록 타입. 학생 서술/선택/순서 답안 */
export const SOLUTION_BLOCK_TYPES = [
  "textarea",
  "card_select_single",
  "card_select_multi",
  "drag_reorder",
  //   풀이영역 신규 2종.
  "essential_triage",
  "budget_allocation",
] as const;

/** 자료영역(좌측 탭) 블록 타입. 조작 입력 유지(N2-b), 학생은 정오답 모름 */
export const MATERIAL_INTERACTION_BLOCK_TYPES = [
  "pin_keypad",
] as const;

/**
 * 삭제 대상(사용자 미언급). 신규 생성에서 제외, 기존 데이터는 렌더 skip.
 * 빈 탭/콘솔 에러 금지( design guide G7).
 */
export const DELETED_BLOCK_TYPES = [
  "drag_pin",
  "material_tabs",
  "video_cinematic",
] as const;

/** 풀이영역(우측 수직 스택)에 노출할 블록만 추린다. 삭제 타입은 제외. */
export function selectSolutionBlocks(
  blocks: InteractionBlock[]
): InteractionBlock[] {
  return blocks.filter((b) =>
    (SOLUTION_BLOCK_TYPES as readonly string[]).includes(b.type)
  );
}

/** 자료영역(좌측 탭)에 노출할 인터랙션 블록만 추린다. 삭제 타입은 제외. */
export function selectMaterialInteractionBlocks(
  blocks: InteractionBlock[]
): InteractionBlock[] {
  return blocks.filter((b) =>
    (MATERIAL_INTERACTION_BLOCK_TYPES as readonly string[]).includes(b.type)
  );
}

/** 삭제 대상(렌더 skip) 타입인지. 빈 탭·깨진 렌더 방지용 가드. */
export function isDeletedBlockType(type: string): boolean {
  return (DELETED_BLOCK_TYPES as readonly string[]).includes(type);
}

// ────────────────────────────────────────────────────────────────────────────
// (후속, 버그①):
//   PIN/순서맞추기 자료블록 빈 화면 버그 정정.
//
// 어드민(producer)은 pin_keypad 와 legacy drag_reorder 를 자료영역(MATERIAL_AREA_TYPES)
// 으로 분류해 **materialBlocksJson** 에 저장한다. 학생(consumer) play.tsx 는 이 JSON 을
// parseMaterialBlocks → MaterialBlockRenderer 로 라우팅하는데, 그 렌더러에는
// pin_keypad/drag_reorder case 가 없어 `default → null` 로 빈 탭이 떴다.
//
// 따라서 materialBlocksJson 파싱 결과를 두 갈래로 쪼갠다.
//   - 순수 자료블록(tabs/cards/image/text)  → MaterialBlockRenderer (기존 경로)
//   - 조작 블록(pin_keypad/drag_reorder)     → InteractionBlockRenderer (case 보유)
//
// ★ 조작 블록은 InteractionBlock 과 동일 구조로 직렬화돼 있다(어드민이 같은 serializer 사용).
//   따라서 InteractionBlock 으로 캐스팅해 InteractionBlockRenderer 에 그대로 넘긴다.
// ★ interactionBlocksJson 출처의 pin_keypad(selectMaterialInteractionBlocks)와
//   합칠 때는 호출자(play.tsx)가 blockId 기준으로 중복을 제거한다.
// ────────────────────────────────────────────────────────────────────────────

/**
 * materialBlocksJson 파싱 결과에서 **순수 자료블록**(MaterialBlockRenderer 가 렌더할 수 있는
 * tabs/cards/image/text)만 추린다. pin_keypad/drag_reorder 같은 조작 블록과 삭제 타입은 제외.
 */
export function selectMaterialDisplayBlocks(
  blocks: MaterialBlock[]
): MaterialBlock[] {
  return blocks.filter((b) =>
    (MATERIAL_DISPLAY_BLOCK_TYPES as readonly string[]).includes(b.type)
  );
}

/**
 * materialBlocksJson 안에 섞여 들어온 **조작 블록**(pin_keypad/drag_reorder)을 추려
 * InteractionBlock 배열로 돌려준다. 삭제 타입은 제외.
 * 어드민이 InteractionBlock 과 동일 구조로 직렬화하므로 캐스팅이 안전하다.
 */
export function selectMaterialOperationBlocks(
  blocks: MaterialBlock[]
): InteractionBlock[] {
  return blocks.filter((b) =>
    (MATERIAL_OPERATION_BLOCK_TYPES as readonly string[]).includes(b.type)
  ) as unknown as InteractionBlock[];
}

/** MaterialBlockRenderer 가 렌더하는 순수 자료블록 타입. */
export const MATERIAL_DISPLAY_BLOCK_TYPES = [
  "tabs",
  "cards",
  "image",
  "text",
] as const;

/**
 * materialBlocksJson 에 들어올 수 있는 조작 블록 타입.
 * pin_keypad 는 자료영역 유지(Q2), legacy drag_reorder 도 좌측 탭에서 인터랙티브 렌더.
 */
export const MATERIAL_OPERATION_BLOCK_TYPES = [
  "pin_keypad",
  "drag_reorder",
] as const;

/** 캐릭터 대화 배열. 키 이름은 `char`(소문자 "coding"/"biyak"). */
export interface CharacterDialogueLine {
  char?: string;
  text: string;
}

/** introCharacterDialogueJson 문자열을 파싱한다. 배열이 아니면(파싱 실패 포함) 빈 배열을 반환한다. */
export function parseCharacterDialogue(
  json: string | null | undefined
): CharacterDialogueLine[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed as CharacterDialogueLine[];
    return [];
  } catch {
    return [];
  }
}

/** 시나리오 완료 보상 카드. 모든 키 선택(undefined 허용), 누락 시 해당 영역 미렌더. */
export interface OutroCardItem {
  /** 아이템 아이콘 (이모지 문자열) */
  icon: string;
  /** 아이템 이름 (학생 노출) */
  name: string;
}

export interface OutroCard {
  /** 호칭 (예: "단서 추리꾼") */
  title?: string;
  /** 호칭 부제 (예: "탐험의 시작") */
  subtitle?: string;
  /** 획득한 아이템 0~5개. 빈/없으면 "획득한 아이템" 영역 자체 숨김. */
  items?: OutroCardItem[];
  /** 📜 다음 관문 티저. 없으면 티저 블록 숨김. */
  nextTeaser?: string;
  /** 캐릭터 말풍선 멘트. 없으면 말풍선 멘트 줄 생략. */
  message?: string;
}

/** outroCardJson 문자열을 파싱한다. 파싱 실패 시 null을 반환한다(보상 카드 영역 미렌더). */
export function parseOutroCard(json: string | null | undefined): OutroCard | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as OutroCard;
  } catch {
    return null;
  }
}

/**
 * home "오늘의 서술형 문제" 카드 응답. `GET /app/essay-learning/chapters/preview`
 *
 * 미완료 챕터만 골라오므로 submitDate는 BE 응답에 없다.
 * hasTempSave/completedQuestionCount/submitDate는 호출처 호환을 위해 옵셔널 유지.
 */

export interface EssayPreviewResponse {
  essayChapterId: number;
  chapterName: string;
  questionCount: number;
  hasTempSave?: boolean;
  completedQuestionCount?: number;
  /** 미션(시나리오) 수. 홈 진행 막대의 점 개수 = scenarioCount + 1. 구 BE 응답이면 undefined. */
  scenarioCount?: number;
  /** 제출 완료된 미션 수. 홈 코딩이 위치의 기준. */
  completedScenarioCount?: number;
  submitDate?: string | null;
  /**
   * 이 서술형 챕터 인스턴스의 원본 템플릿 id(= 검사 게이트 examRef, ESSAY).
   * `TodayProblem`이 `play.tsx` 이동 시 `templateId` 쿼리로 그대로 넘긴다 — 검사 게이트는
   * 홈이 아니라 `play.tsx`가 튜토리얼 종료 시점에 조회한다(2026-09-20 결정).
   * ★ BE preview 응답(`EssayChapterPreviewResponse`)이 아직 노출하지 않음 →
   *   undefined 인 동안은 게이트가 fail-open(스킵). BE 가 템플릿 id 를 추가하면 자동 발동.
   */
  essayChapterTemplateId?: number;
}
